/* ============================================================================
   CNC WORLD HUB — progressive enhancement only.
   Page is fully functional + readable with this file absent:
     · FAQ = native <details> (multi-open). · Read selector = radio + CSS.
   This file adds:
     1. Sticky anchor-nav backdrop-on-stick + scroll-spy active highlight.
     2. Hero parallax + a dense, lattice-broken star-field (reduced-motion safe).
     3. Filter: nav "Filter" reveals a search that filters cast + places.
     4. FAQ accordion → single-open + animated (grid-rows), reduced-motion safe.
     5. Read selector → ARIA tablist, active-tab centering, card reveal, arrows.
   No deps. Defers. Fails silent. Honours prefers-reduced-motion.
   ============================================================================ */
(function () {
  'use strict';
  var root = document.querySelector('[data-whub-root]');
  if (!root) return;
  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var accBusy = false;

  /* center a child horizontally inside its OWN scroll container — never scrolls the page
     (replaces scrollIntoView, whose block:'nearest' yanked the page down to a mid-page
     default-checked tab on load → "lands halfway down" bug). */
  function centerX(container, el) {
    if (!container || !el || !container.scrollBy) return;
    var c = container.getBoundingClientRect(), e = el.getBoundingClientRect();
    var delta = (e.left + e.width / 2) - (c.left + c.width / 2);
    if (Math.abs(delta) < 2) return;
    container.scrollBy({ left: delta, top: 0, behavior: reduceMotion ? 'auto' : 'smooth' });
  }
  /* center AFTER the expand transition (flex-basis .5s) settles — measuring mid-transition
     centers the un-expanded size, leaving the opened card half off-screen on mobile (the
     amount off depends on its position in the row). Run it twice to catch the settle. */
  function centerXSoon(container, el) {
    setTimeout(function () { centerX(container, el); }, 300);
    setTimeout(function () { centerX(container, el); }, 560);
  }

  /* ── 1. Sticky anchor nav: backdrop-on-stick + scroll-spy ───────────────── */
  var nav = root.querySelector('[data-whub-anchornav]');
  if (nav) {
    var sentinel = document.createElement('div');
    sentinel.setAttribute('aria-hidden', 'true');
    sentinel.style.cssText = 'position:absolute;height:1px;width:1px;';
    nav.parentNode.insertBefore(sentinel, nav);
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (e) { nav.classList.toggle('is-stuck', !e[0].isIntersecting); }, { threshold: [0] }).observe(sentinel);
    }
    var links = {};
    nav.querySelectorAll('[data-whub-spy]').forEach(function (a) { links[a.getAttribute('data-whub-spy')] = a; });
    var zones = root.querySelectorAll('[data-whub-zone]');
    if ('IntersectionObserver' in window && zones.length) {
      var current = null;
      var spy = new IntersectionObserver(function (entries) {
        if (accBusy) return; /* don't let an accordion height change flicker the spy */
        entries.forEach(function (e) {
          if (!e.isIntersecting) return;
          var id = e.target.getAttribute('data-whub-zone');
          if (!links[id] || id === current) return;
          if (current && links[current]) links[current].classList.remove('is-active');
          links[id].classList.add('is-active');
          current = id;
        });
      }, { rootMargin: '-45% 0px -50% 0px', threshold: 0 });
      zones.forEach(function (z) { spy.observe(z); });
    }
  }

  /* ── 2. Filter: toggle the search, filter cast + place tiles by name ─────── */
  var fToggle = root.querySelector('[data-whub-filter-toggle]');
  var fWrap = root.querySelector('[data-whub-filter]');
  var fInput = root.querySelector('[data-whub-filter-input]');
  if (fToggle && fWrap && fInput) {
    var items = root.querySelectorAll('[data-whub-filter-item]');
    var apply = function (q) {
      q = (q || '').trim().toLowerCase();
      items.forEach(function (el) {
        var hay = el.getAttribute('data-whub-filter-item') || '';
        el.classList.toggle('is-hidden', q !== '' && hay.indexOf(q) === -1);
      });
    };
    fToggle.addEventListener('click', function () {
      var open = fWrap.hasAttribute('hidden');
      if (open) { fWrap.removeAttribute('hidden'); fToggle.setAttribute('aria-expanded', 'true'); fInput.focus(); }
      else { fWrap.setAttribute('hidden', ''); fToggle.setAttribute('aria-expanded', 'false'); fInput.value = ''; apply(''); }
    });
    fInput.addEventListener('input', function () { apply(fInput.value); });
  }

  /* ── 3. Hero parallax (motion only) ─────────────────────────────────────── */
  if (!reduceMotion) {
    var field = root.querySelector('[data-whub-parallax]');
    var hero = root.querySelector('.whub-hero');
    if (field && hero && 'requestAnimationFrame' in window) {
      var ticking = false;
      var applyP = function () {
        var rect = hero.getBoundingClientRect();
        if (rect.bottom > 0 && rect.top < window.innerHeight) {
          field.style.setProperty('--whub-py', Math.round((-rect.top) * 0.16) + 'px');
        }
        ticking = false;
      };
      window.addEventListener('scroll', function () { if (!ticking) { ticking = true; requestAnimationFrame(applyP); } }, { passive: true });
      applyP();
    }
  }

  /* ── 4. Hero star-field: dense, lattice-broken, gentle twinkle ──────────── */
  var starField = root.querySelector('[data-whub-stars]');
  if (starField) {
    var N = 150, frag = document.createDocumentFragment();
    for (var i = 0; i < N; i++) {
      var d = document.createElement('span');
      d.className = 'whub-hero__star';
      var r = Math.random();
      var sz = r > 0.94 ? 2.6 : (r > 0.78 ? 1.8 : 1.0);   /* mostly tiny, a few bright */
      d.style.width = d.style.height = sz + 'px';
      d.style.left = (Math.random() * 100).toFixed(2) + '%';
      d.style.top = (Math.pow(Math.random(), 1.4) * 82).toFixed(2) + '%'; /* denser up top */
      d.style.opacity = (0.30 + Math.random() * 0.62).toFixed(2);
      if (sz > 1.6) d.style.boxShadow = '0 0 ' + (sz + 1).toFixed(0) + 'px ' + (sz / 2).toFixed(1) + 'px rgba(224,207,231,.7)';
      if (!reduceMotion && r > 0.62) { d.setAttribute('data-tw', ''); d.style.setProperty('--tw', (3 + Math.random() * 4).toFixed(1) + 's'); d.style.animationDelay = (Math.random() * 4).toFixed(1) + 's'; }
      frag.appendChild(d);
    }
    starField.appendChild(frag);
  }

  /* ── 5. FAQ accordion: single-open + animated (grid-rows) ────────────────── */
  var acc = root.querySelector('[data-whub-accordion]');
  if (acc) {
    acc.setAttribute('data-enhanced', '');
    var fitems = Array.prototype.slice.call(acc.querySelectorAll('.whub-faqitem'));
    fitems.forEach(function (d) {
      var sum = d.querySelector('.whub-faqitem__q');
      var wrap = d.querySelector('.whub-faqitem__wrap');
      if (d.open) d.setAttribute('data-open', '');
      if (!sum) return;
      sum.addEventListener('click', function (e) {
        e.preventDefault();
        accBusy = true; setTimeout(function () { accBusy = false; }, 420);
        var isOpen = d.hasAttribute('data-open');
        fitems.forEach(function (o) { if (o !== d) { o.removeAttribute('data-open'); o.open = false; } }); /* single-open */
        if (isOpen) {
          d.removeAttribute('data-open');
          if (reduceMotion || !wrap) { d.open = false; }
          else {
            var done = function () { d.open = false; wrap.removeEventListener('transitionend', done); };
            wrap.addEventListener('transitionend', done);
          }
        } else {
          d.open = true; /* into flow first, then animate open next frame */
          if (reduceMotion) { d.setAttribute('data-open', ''); }
          else { requestAnimationFrame(function () { d.setAttribute('data-open', ''); }); }
        }
      });
    });
  }

  /* ── 6. Read selector (book spines): ARIA + active-spine centering + arrows ─ */
  var rsel = root.querySelector('[data-whub-readsel]');
  if (rsel) {
    var radios = Array.prototype.slice.call(rsel.querySelectorAll('.whub-readsel__radio'));
    var tabs = Array.prototype.slice.call(rsel.querySelectorAll('.whub-readsel__spine'));
    var tabstrip = rsel.querySelector('.whub-readsel__shelf');
    var sync = function (focusTab, soon) {
      radios.forEach(function (rd, idx) {
        var tab = tabs[idx];
        if (!tab) return;
        var on = rd.checked;
        tab.setAttribute('aria-selected', on ? 'true' : 'false');
        tab.setAttribute('tabindex', on ? '0' : '-1');
        if (on) {
          if (tabstrip && tabstrip.scrollWidth > tabstrip.clientWidth) (soon ? centerXSoon : centerX)(tabstrip, tab);
          if (focusTab) tab.focus();
        }
      });
    };
    radios.forEach(function (rd) { rd.addEventListener('change', function () { sync(false, true); }); });
    tabs.forEach(function (tab, idx) {
      tab.addEventListener('keydown', function (e) {
        var dir = e.key === 'ArrowRight' ? 1 : (e.key === 'ArrowLeft' ? -1 : 0);
        if (!dir) return;
        e.preventDefault();
        var n = (idx + dir + radios.length) % radios.length;
        if (radios[n]) { radios[n].checked = true; sync(true, true); }
      });
    });
    sync(false);
  }

  /* ── 7. Inner Circle image-accordion: center the expanded panel on mobile ─ */
  var ic = root.querySelector('[data-whub-ic]');
  if (ic) {
    var icRadios = Array.prototype.slice.call(ic.querySelectorAll('.whub-ic__radio'));
    var icRow = ic.querySelector('.whub-ic__row');
    var icPanels = Array.prototype.slice.call(ic.querySelectorAll('.whub-ic__panel'));
    var icSync = function () {
      icRadios.forEach(function (rd, idx) {
        var panel = icPanels[idx];
        if (!panel) return;
        if (rd.checked && icRow && icRow.scrollWidth > icRow.clientWidth) centerXSoon(icRow, panel);
      });
    };
    icRadios.forEach(function (rd) { rd.addEventListener('change', icSync); });
  }
})();
